import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, formatApiError } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const LOGO_STYLE = { backgroundImage: "url('/logo.png')", backgroundSize: "205% auto", backgroundPosition: "center 5%", backgroundRepeat: "no-repeat" };

const emptyOrg = { organization: "", states: [], county: "", service_area: "", org_type: "", services: [], payer_types: [], license_type: "", programs: [] };

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", account_type: "family" });
  const [org, setOrg] = useState(emptyOrg);
  const [tax, setTax] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isBiz = form.account_type === "business";

  useEffect(() => {
    if (isBiz && !tax) api.get("/compliance/taxonomy").then((r) => setTax(r.data)).catch(() => {});
  }, [isBiz, tax]);

  const toggle = (field, val) => setOrg((o) => ({ ...o, [field]: o[field].includes(val) ? o[field].filter((x) => x !== val) : [...o[field], val] }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const orgProfile = isBiz ? org : null;
      await register(form.name, form.email, form.password, form.account_type, orgProfile);
      toast.success("Account created!");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  const Chips = ({ field, items }) => (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => (
        <button type="button" key={it.id || it.slug} data-testid={`org-${field}-${it.id || it.slug}`}
          onClick={() => toggle(field, it.id || it.slug)}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${org[field].includes(it.id || it.slug) ? "bg-teal-600 text-white border-teal-600" : "bg-white text-blue-900 border-blue-900/10 hover:border-teal-500"}`}>
          {it.name}
        </button>
      ))}
    </div>
  );

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16" style={isBiz ? { maxWidth: "42rem" } : undefined}>
      <div className="text-center mb-8">
        <span className="inline-block w-16 h-16 rounded-2xl bg-white shadow-sm ring-1 ring-blue-900/10 mx-auto mb-4" style={LOGO_STYLE} />
        <h1 className="font-display text-3xl font-bold text-blue-950">Create your account</h1>
        <p className="mt-2 text-slate-600">Join the T5 care ecosystem.</p>
      </div>
      <form onSubmit={submit} data-testid="register-form" className="bg-white rounded-2xl p-8 shadow-lg border border-blue-900/5 space-y-4">
        {error && <div data-testid="register-error" className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-2.5">{error}</div>}
        <div>
          <Label>I am a…</Label>
          <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
            <SelectTrigger data-testid="register-account-type" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="family">Family / individual seeking care</SelectItem>
              <SelectItem value="caregiver">Caregiver</SelectItem>
              <SelectItem value="business">Business / organization</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label htmlFor="r-name">Full name</Label><Input id="r-name" data-testid="register-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
        <div><Label htmlFor="r-email">Email</Label><Input id="r-email" type="email" data-testid="register-email-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
        <div><Label htmlFor="r-pw">Password</Label><Input id="r-pw" type="password" data-testid="register-password-input" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" /></div>

        {isBiz && (
          <div className="pt-2 border-t border-blue-900/5 space-y-4" data-testid="org-profile-section">
            <div className="flex items-center gap-2"><Building2 className="w-5 h-5 text-teal-600" /><h2 className="font-display font-bold text-blue-950">Organization compliance profile</h2></div>
            <p className="text-xs text-slate-500 -mt-2">Tell us where and how you operate — we'll tailor multi-state compliance for you.</p>
            <div><Label>Organization name</Label><Input data-testid="org-name" value={org.organization} onChange={(e) => setOrg({ ...org, organization: e.target.value })} className="mt-1.5" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>County</Label><Input data-testid="org-county" value={org.county} onChange={(e) => setOrg({ ...org, county: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Service area</Label><Input data-testid="org-service-area" value={org.service_area} onChange={(e) => setOrg({ ...org, service_area: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div>
              <Label>Organization type</Label>
              <Select value={org.org_type} onValueChange={(v) => setOrg({ ...org, org_type: v })}>
                <SelectTrigger data-testid="org-type" className="mt-1.5"><SelectValue placeholder="Select type…" /></SelectTrigger>
                <SelectContent className="max-h-72">{(tax?.org_types || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>States operated in <span className="text-slate-400 font-normal">(select all)</span></Label><div className="mt-2 max-h-40 overflow-y-auto"><Chips field="states" items={tax?.states || []} /></div></div>
            <div><Label>Services offered</Label><div className="mt-2"><Chips field="services" items={tax?.care_settings || []} /></div></div>
            <div><Label>Payer / program types</Label><div className="mt-2"><Chips field="payer_types" items={tax?.payer_types || []} /></div></div>
            <div><Label>License type</Label><Input data-testid="org-license" value={org.license_type} onChange={(e) => setOrg({ ...org, license_type: e.target.value })} className="mt-1.5" placeholder="e.g. Home Care Agency License" /></div>
          </div>
        )}

        <Button type="submit" disabled={loading} data-testid="register-submit-button" className="w-full h-12 bg-blue-900 hover:bg-blue-950 text-white">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create Account"}</Button>
        <p className="text-center text-sm text-slate-500">Have an account? <Link to="/login" data-testid="go-login-link" className="text-blue-800 font-medium">Sign in</Link></p>
      </form>
    </div>
  );
}
